// Invite a teammate (WEB_PLAN.md §5.4), the port of InviteMemberSheet (GroundTeam/Views/Sync/InviteMemberSheet.swift)
// and SyncCenter.invite (GroundTeam/Sync/SyncCenter.swift:1238), as pure functions plus one orchestrator that takes
// its I/O as arguments (unit-tested with fakes):
// 1. make the `users` item match the invitation: create a new person, or patch an existing profile's role (and email
//    when one was typed);
// 2. wait until the server has that version of the profile (an invitation must never point at a profile phones
//    cannot read yet, and its role must be the one the profile shows);
// 3. `invite_member` → the invitation with its code, shown in the share dialog.
// Nobody gets a login from the admin: each person creates their own account and joins with the code or the email.

import { serverErrorKind, teamErrorMessage } from '@/auth/errors';
import type { TeamPayload, UserPayload } from '@/contracts/models';
import { newUser } from '@/contracts/models';
import { USER_ROLE_TITLES } from '@/contracts/enums';
import type { JsonObject, Role, TeamInvitation } from '@/contracts/wire';
import { parseWireDate } from '@/contracts/wireDate';
import type { Collection } from '@/contracts/collections';
import { isEmailTaken } from '@/domain/team';
import { byId, compareLocalized, fullName, idKey, lookup, sameId, trimAll, trimSpaces } from '@/domain/util';
import type { SyncEngine } from '@/sync/engine';
import { patch, type Patch } from '@/sync/patch';

// MARK: - Form

export type InviteSource = 'existing' | 'new';

/** Picker order (`UserRole.allCases`). */
export const INVITE_ROLES: readonly Role[] = ['admin', 'manager', 'rep'];

export const INVALID_EMAIL_TEXT = 'Enter a valid email address, or leave it empty.';
export const EMAIL_TAKEN_TEXT = 'Another profile already uses this email.';

/**
 * `emailProblem`: null when the (optional) email is fine. One `@` with text on both sides and no spaces, and no other
 * profile may use it (`isEmailTaken`, case-insensitive). `excludingId` is the profile being invited.
 */
export function inviteEmailProblem(
  email: string,
  users: readonly Pick<UserPayload, 'id' | 'email'>[],
  excludingId: string | null | undefined,
): string | null {
  const e = trimAll(email);
  if (e === '') return null;
  const parts = e.split('@');
  if (parts.length !== 2 || parts[0] === '' || parts[1] === '' || /\s/.test(e)) return INVALID_EMAIL_TEXT;
  if (isEmailTaken(users as readonly UserPayload[], e, excludingId)) return EMAIL_TAKEN_TEXT;
  return null;
}

export interface InviteFormState {
  source: InviteSource;
  selectedProfileId: string | null;
  firstName: string;
  email: string;
  sending: boolean;
}

/** `canSend`: a profile picked (or a first name typed), a usable email or none, and not already sending. */
export function canSendInvite(form: InviteFormState, emailProblem: string | null): boolean {
  if (form.sending || emailProblem !== null) return false;
  return form.source === 'existing' ? form.selectedProfileId !== null : trimSpaces(form.firstName) !== '';
}

/**
 * `candidates`: profiles that can be invited, by name. Active, without an account (`memberProfileIds`), not the
 * admin's own profile and not the one this dialog created on an earlier try (that one is under "New person").
 */
export function inviteCandidates(
  users: readonly UserPayload[],
  memberProfileIds: ReadonlySet<string>,
  meId: string | null | undefined,
  createdProfileId: string | null | undefined,
): UserPayload[] {
  return users
    .filter(
      (u) => u.isActive && !memberProfileIds.has(idKey(u.id)) && !sameId(u.id, meId) && !sameId(u.id, createdProfileId),
    )
    .sort((a, b) => compareLocalized(fullName(a), fullName(b)));
}

// MARK: - Step 1: the profile

/** What the server copy of the profile must say before the invitation is created. */
export interface ProfileExpectation {
  role: Role;
  /** Checked (case-insensitively) only when the invitation sets the profile's email. */
  email?: string;
}

export interface PreparedProfile {
  profileId: string;
  /** Empty when the profile already matches. */
  patches: Patch[];
  /** A new `users` item was made (keep its id: a retry patches it instead of creating another). */
  created: boolean;
  expect: ProfileExpectation;
}

export interface PrepareInput {
  source: InviteSource;
  /** Existing profile: the picked one (decodable payload). */
  selected?: UserPayload | null;
  /** New person: the profile an earlier, failed try created (reused on retry), if it still exists. */
  created?: UserPayload | null;
  firstName: string;
  lastName: string;
  /** Trimmed email or null (optional). */
  email: string | null;
  role: Role;
  /** The admin's own profile: a new person joins their team and reports to them. */
  me?: Pick<UserPayload, 'id' | 'role' | 'teamId'> | null;
  teams: readonly Pick<TeamPayload, 'id' | 'managerId'>[];
  /** Avatar color for a new person (random palette color on iOS). */
  colorHex: string;
}

const asJson = (v: object): JsonObject => v as unknown as JsonObject;

/**
 * `prepareProfile(email:role:)`. An empty email leaves an existing profile's email alone (it is contact info, not the
 * invitation); a new person's email is whatever was typed. Null for "existing" without a picked profile.
 */
export function prepareInviteProfile(input: PrepareInput): PreparedProfile | null {
  const { email, role } = input;
  const expect: ProfileExpectation = email !== null ? { role, email } : { role };
  if (input.source === 'existing') {
    const user = input.selected;
    if (!user) return null;
    const values: Record<string, string> = {};
    if (email !== null && user.email.toLowerCase() !== email.toLowerCase()) values.email = email;
    if (user.role !== role) values.role = role;
    return {
      profileId: user.id,
      patches: Object.keys(values).length > 0 ? [patch.set('users', user.id, values)] : [],
      created: false,
      expect,
    };
  }

  const first = trimSpaces(input.firstName);
  const last = trimSpaces(input.lastName);
  const address = email ?? '';
  if (input.created) {
    const u = input.created;
    const values: Record<string, string> = {};
    if (u.firstName !== first) values.firstName = first;
    if (u.lastName !== last) values.lastName = last;
    if (u.email !== address) values.email = address;
    if (u.role !== role) values.role = role;
    return {
      profileId: u.id,
      patches: Object.keys(values).length > 0 ? [patch.set('users', u.id, values)] : [],
      created: false,
      expect: { role, email: address },
    };
  }

  // WEB_PLAN.md §5.4 "New person": the creator's team; the creator as manager when they manage people (admins and
  // managers, `canManageTeam`), else that team's manager.
  const me = input.me ?? null;
  const team = me?.teamId ? lookup(byId(input.teams), me.teamId) : undefined;
  const managerId = me && me.role !== 'rep' ? me.id : team?.managerId;
  const user = newUser({
    firstName: first,
    lastName: last,
    role,
    email: address,
    isActive: true,
    colorHex: input.colorHex,
    ...(me?.teamId ? { teamId: me.teamId } : {}),
    ...(managerId ? { managerId } : {}),
  });
  return { profileId: user.id, patches: [patch.create('users', user.id, asJson(user))], created: true, expect: { role, email: address } };
}

/** The server copy of the profile carries the invitation's role (and email, when it sets one). */
export function matchesExpectation(payload: JsonObject, expect: ProfileExpectation): boolean {
  if (payload.role !== expect.role) return false;
  if (expect.email === undefined) return true;
  const email = typeof payload.email === 'string' ? payload.email : '';
  return trimAll(email).toLowerCase() === trimAll(expect.email).toLowerCase();
}

// MARK: - Step 2: wait for the server

/** Why the profile did not reach the server. */
export class InviteStepError extends Error {
  readonly kind: 'timeout' | 'refused';
  constructor(kind: 'timeout' | 'refused', message: string) {
    super(message);
    this.name = 'InviteStepError';
    this.kind = kind;
  }
}

export const PROFILE_TIMEOUT_TEXT =
  "The profile isn't saved on the server yet, so no invitation was created. Check your connection and try again.";
export const PROFILE_REFUSED_TEXT = "The server didn't save the profile, so no invitation was created.";

/** What the waiter reads; `engineServerCopySource` adapts the sync engine and its replica to it. */
export interface ServerCopySource {
  /** The item's local edit still waiting for the server, or null when there is none. */
  pending(id: string): { failed: boolean; error?: string } | null;
  /** The server's copy (a row with version > 0), or null. */
  server(id: string): JsonObject | null;
  /** Called after every change to the collection. */
  subscribe(listener: () => void): () => void;
  /** The engine's notices (refused edits: forbidden, last admin, invalid, rejected, deleted elsewhere). */
  onNotice(listener: (notice: { id: string; message: string }) => void): () => void;
}

export interface WaitOptions {
  timeoutMs?: number;
  /**
   * When the local edit is gone but the server copy does not match (the server refused it and its copy was restored),
   * how long to wait for the engine's notice, whose words are better than ours.
   */
  graceMs?: number;
}

/**
 * Resolves once the item has no local edit left and the server copy passes `accept`; rejects with an InviteStepError
 * when the edit failed, the server refused or restored it, or nothing happened within `timeoutMs` (offline).
 */
export function waitForServerCopy(
  source: ServerCopySource,
  id: string,
  accept: (payload: JsonObject) => boolean,
  options: WaitOptions = {},
): Promise<void> {
  const timeoutMs = options.timeoutMs ?? 20_000;
  const graceMs = options.graceMs ?? 300;
  return new Promise<void>((resolve, reject) => {
    let done = false;
    let grace: ReturnType<typeof setTimeout> | undefined;
    const cleanups: (() => void)[] = [];
    const finish = (error?: Error) => {
      if (done) return;
      done = true;
      for (const c of cleanups) c();
      if (error) reject(error);
      else resolve();
    };
    const check = () => {
      if (done) return;
      const pending = source.pending(id);
      if (pending) {
        if (pending.failed) {
          finish(new InviteStepError('refused', pending.error ? `${PROFILE_REFUSED_TEXT} (${pending.error})` : PROFILE_REFUSED_TEXT));
        }
        return; // still on its way
      }
      const server = source.server(id);
      if (server && accept(server)) {
        finish();
        return;
      }
      grace ??= setTimeout(() => finish(new InviteStepError('refused', PROFILE_REFUSED_TEXT)), graceMs);
    };
    const timer = setTimeout(() => finish(new InviteStepError('timeout', PROFILE_TIMEOUT_TEXT)), timeoutMs);
    cleanups.push(() => clearTimeout(timer), () => grace !== undefined && clearTimeout(grace));
    cleanups.push(source.subscribe(check));
    cleanups.push(
      source.onNotice((notice) => {
        if (sameId(notice.id, id)) finish(new InviteStepError('refused', notice.message || PROFILE_REFUSED_TEXT));
      }),
    );
    check();
  });
}

/**
 * The engine's replica and notices for one collection, as the waiter reads them. sync/ has no "wait until this row is
 * saved" call, so this reads the outbox entry (still on its way, or failed) and the server row (version > 0). Kept
 * here, React-free, so it is tested against the real engine and the in-memory server.
 */
export function engineServerCopySource(engine: Pick<SyncEngine, 'replica' | 'onNotice'>, collection: Collection): ServerCopySource {
  return {
    pending(id) {
      const entry = engine.replica.getEntry(collection, id);
      if (!entry) return null;
      return entry.error !== undefined ? { failed: entry.state === 'failed', error: entry.error } : { failed: entry.state === 'failed' };
    },
    server(id) {
      const row = engine.replica.getRow(collection, id);
      return row && row.version > 0 ? row.payload : null;
    },
    subscribe: (listener) => engine.replica.subscribe(collection, listener),
    onNotice: (listener) =>
      engine.onNotice((notice) => {
        if (notice.collection === collection) listener({ id: notice.id, message: notice.message });
      }),
  };
}

// MARK: - Step 3: the invitation

export interface InviteMemberParams {
  p_company: string;
  /** Lower-cased, or null for an invite-code-only invitation. */
  p_email: string | null;
  p_profile_id: string;
  p_role: Role;
}

export interface InviteDeps {
  /** `engine.save` (throws SyncNotReadyError or on an invalid patch: nothing is stored then). */
  save: (patches: readonly Patch[]) => unknown;
  /** Step 2 for one profile (waitForServerCopy over the engine). */
  waitForProfile: (profileId: string, accept: (payload: JsonObject) => boolean) => Promise<void>;
  /** `rpc('invite_member', params)`. */
  inviteMember: (params: InviteMemberParams) => Promise<TeamInvitation>;
}

/** Steps 1–3 for a prepared profile. Any failure stops the flow before the next step. */
export async function sendInvitation(
  prepared: PreparedProfile,
  params: { companyId: string; email: string | null; role: Role },
  deps: InviteDeps,
): Promise<TeamInvitation> {
  if (prepared.patches.length > 0) deps.save(prepared.patches);
  await deps.waitForProfile(prepared.profileId, (payload) => matchesExpectation(payload, prepared.expect));
  const email = params.email !== null ? trimAll(params.email).toLowerCase() : '';
  return deps.inviteMember({
    p_company: params.companyId.toLowerCase(),
    p_email: email === '' ? null : email,
    p_profile_id: prepared.profileId.toLowerCase(),
    p_role: params.role,
  });
}

/** `CenterError.inviteeAlreadyMember`: here `already_member` is about the invitee, not this account. */
export const INVITEE_ALREADY_MEMBER_TEXT = 'Someone with this email or profile is already on the team.';

/** Error text for the invite, revoke and remove actions, in the iOS words. */
export function inviteErrorMessage(error: unknown, companyName?: string | null): string {
  if (error instanceof InviteStepError) return error.message;
  if (error instanceof Error && error.name === 'SyncNotReadyError') return error.message;
  if (serverErrorKind(error) === 'already_member') return INVITEE_ALREADY_MEMBER_TEXT;
  return teamErrorMessage(error, companyName);
}

// MARK: - Share dialog text (InviteSentView / InviteCodeCard)

/** `InviteSentView.who`: the profile's name, else the invited email, else "Your teammate". */
export function invitedWho(name: string | null | undefined, invitation: Pick<TeamInvitation, 'email'>): string {
  if (name && trimSpaces(name) !== '') return trimSpaces(name);
  const email = invitation.email ? trimAll(invitation.email) : '';
  return email !== '' ? email : 'Your teammate';
}

/** `InviteSentView.joinDetail`. */
export function inviteJoinDetail(invitation: Pick<TeamInvitation, 'role' | 'email'>): string {
  const noun = (USER_ROLE_TITLES[invitation.role] ?? invitation.role).toLowerCase();
  const article = /^[aeiou]/.test(noun) ? 'an' : 'a';
  let text = `They join as ${article} ${noun} after they create an account (or sign in) and confirm the invitation.`;
  const email = invitation.email ? trimAll(invitation.email) : '';
  if (email !== '') text += ` If they sign up with ${email}, it shows up by itself.`;
  return text;
}

/** `InviteCodeCard` expiry line, or null without an expiry. */
export function inviteExpiryText(
  invitation: Pick<TeamInvitation, 'expires_at'>,
  now: Date = new Date(),
  locale?: string,
): { text: string; expired: boolean } | null {
  const expires = parseWireDate(invitation.expires_at);
  if (!expires) return null;
  const date = new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(expires);
  const expired = expires.getTime() < now.getTime();
  return expired
    ? { text: `Expired on ${date}. Revoke it and invite them again.`, expired }
    : { text: `Expires on ${date}`, expired };
}

/** The code read character by character ("A B C D …"), for screen readers. */
export function spelledCode(code: string): string {
  return Array.from(code.replace(/[^0-9A-Za-z]/g, '').toUpperCase()).join(' ');
}

/**
 * `mailto:` with the invitation text, because the server sends no email itself. Addressed to the invited email when
 * there is one; otherwise the admin picks the recipient in their mail app.
 */
export function inviteMailto(invitation: Pick<TeamInvitation, 'email'>, message: string, companyName?: string | null): string {
  const to = invitation.email ? trimAll(invitation.email) : '';
  const company = companyName && trimAll(companyName) !== '' ? trimAll(companyName) : 'our team';
  const subject = `Join ${company} on Ground Team`;
  return `mailto:${encodeURIComponent(to).replace(/%40/g, '@')}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(message)}`;
}

/** `revokeMessage` of the confirmation. */
export function revokeMessage(invitation: Pick<TeamInvitation, 'email'> | null | undefined): string {
  const email = invitation?.email ? trimAll(invitation.email) : '';
  return email !== ''
    ? `${email} won't be able to join with this invitation, and its invite code stops working.`
    : 'Its invite code stops working. Nobody can join with it.';
}

export const REMOVE_MEMBER_MESSAGE =
  "They lose access right away and the company's data is removed from their iPhone the next time it syncs. Their profile and history stay.";
